// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useRef, useLayoutEffect } from 'react'
import { useForm } from 'react-hook-form'
import FunnelProgress from './funnel-progress'
import FormStep from './form-step'
import { setConfig, updateConsents } from '@uswitch/rvuconsent'
import { useComponentTracking } from '../../utils/component-events'
import { whitelist } from '../../utils/form'
import { getValue } from '../../utils/product-schema'
import { moduleEntity } from '../helpers/modules'
import { EVEntry } from '../helpers/react'
import yieldToMain from '../../utils/yield-to-main'

const Form = (props) => {
  const {
    schema: {
      steps = [],
      action,
      method,
      formName,
      formId,
      defaultInputs,
      showProgressBar = false,
      withBackButton = false,
      variant = 'default',
      disableButtonUntilValid = false,
      rvuconsent = null,
    },
    apiPath,
    clientId,
    brand,
    submit,
    addressLookup,
    activeIndex,
    goForward,
    goBack,
    goToFirst,
    goToLast,
    goToPage,
    goToUrl,
    query = {},
    completionState,
    updateCompletionState,
    editFromSummary,
    trackInteraction = () => {},
    segmentTracking,
    fieldTracking,
    elasticSearchData,
    url,
    widget,
    sponsoredBy,
    module,
    target = '_blank',
  } = props

  const numberFormattedQuery = Object.entries(query).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [key]: !isNaN(parseInt(val, 10)) ? new Intl.NumberFormat('en-GB').format(val) : val,
    }),
    {},
  )

  const defaultValues = {
    ...defaultInputs,
    ...numberFormattedQuery,
  }

  const { register, handleSubmit, errors, setValue, trigger, formState, watch, getValues } =
    useForm({
      mode: 'all',
      defaultValues,
    })
  const form = useRef(null)
  const { isDirty, isValid } = formState

  const trackComponent = useComponentTracking()

  const trackAndSubmit = async (data) => {
    const submitForm = async (data) => {
      if (segmentTracking) {
        await segmentTracking()
      }

      return submit ? submit(data, form) : form.current.submit()
    }

    if (!rvuconsent) return submitForm(data)

    const fields = steps
      .flatMap((step) =>
        step.fields.map((field) => (field.type === 'wrapper' ? field.children : field)),
      )
      .flat()
    Promise.all(
      fields.map(async (field) => {
        if (!field.consentName) return
        const isChecked = data[`${field.name}-checkbox`]
        const isConsented = field.softOptIn ? !isChecked : isChecked
        if (!isConsented && !field.optionalConsent && !field.softOptIn) return

        return updateConsents(
          {
            email: data[field.name],
            consents:
              isConsented || field.softOptIn
                ? [
                    {
                      name: field.consentName,
                      status: isConsented,
                      source: rvuconsent.source,
                      sourceUrl: window.location.href,
                    },
                  ]
                : [],
          },
          true,
        )
      }),
    ).then(() => submitForm(data))
    await yieldToMain()
    trackInteraction('GA Form Submitted', 'Form Interaction', `${activeIndex}`)
    trackComponent('Form Event', 'formSubmitted', {
      formName,
      formId,
      ...whitelist(data, steps),
    })
  }

  const progressPhases = steps.reduce((acc, { id, funnelTitle = '' }) => {
    return [...acc, { key: id, title: funnelTitle }]
  }, [])
  const progressIncrement = Math.round(100 / steps.length)

  // set it globally before render so you can access it elsewhere
  useLayoutEffect(() => {
    if (rvuconsent) {
      setConfig({
        apiPath: apiPath,
        clientId: clientId,
        brand,
      })
    }
  }, [apiPath, brand, clientId, rvuconsent])

  // if action has value from ES then get url and add query params as hidden field inputs
  const newAction = action?.valueFrom ? getValue(action, elasticSearchData) : action
  const newFields: any = []
  try {
    for (const [key, value] of new URL(newAction).searchParams.entries()) {
      newFields.push({ name: key, type: 'hidden', value: value })
    }
    newFields.push({ name: 'page', type: 'hidden', value: url })
  } catch {}

  const productType = query.productType

  const updateFieldWithProductType = (fields, productType) => {
    return fields.map((field) => {
      if (field.name === 'productType') {
        return { ...field, value: productType }
      }
      return field
    })
  }

  const updateStepFields = (step, newFields, productType) => {
    const fieldsByName = new Map(step.fields.concat(newFields).map((field) => [field.name, field]))
    let updatedFields = Array.from(fieldsByName.values())

    if (productType) {
      updatedFields = updateFieldWithProductType(updatedFields, productType)
    }

    return {
      ...step,
      fields: updatedFields,
    }
  }

  const newSteps = steps.map((step) => updateStepFields(step, newFields, productType))

  return (
    <form
      name={`multi-step-${formName}`}
      action={newAction}
      method={method}
      onSubmit={handleSubmit(trackAndSubmit)}
      ref={form}
      className={`Form ${'Form__' + variant}`}
      id={formId}
      target={target}
    >
      {showProgressBar && (
        <div
          className={`FormFunnel${
            withBackButton && activeIndex !== 0 ? ' FormFunnelWithMargin' : ''
          }`}
        >
          <FunnelProgress
            phases={progressPhases}
            currentPhaseKey={progressPhases[activeIndex]?.key || ''}
            progress={(progressIncrement * activeIndex) / 100}
            variant='percentage'
          />
        </div>
      )}

      {newSteps.map((step, i) => {
        return (
          <FormStep
            stepIndex={i}
            key={`form-step-${i}`}
            module={module}
            {...{
              step,
              register,
              errors,
              disableButtonUntilValid,
              setValue,
              trigger,
              activeIndex,
              goForward,
              goBack,
              goToFirst,
              goToLast,
              goToPage,
              goToUrl,
              isDirty,
              isValid,
              watch,
              completionState,
              updateCompletionState,
              getValues,
              editFromSummary,
              withBackButton,
              variant,
              query,
              handleSubmit,
              trackAndSubmit,
              addressLookup,
              brand,
              defaultValues,
              fieldTracking,
            }}
            {...props}
          />
        )
      })}
      <div className={`Form__${variant}--poweredByWrapper`}>
        <div>
          {sponsoredBy && (
            <EVEntry
              {...moduleEntity(sponsoredBy)}
              removeTopSpacing={true}
              removeBottomSpacing={true}
            />
          )}
        </div>
        <div>
          {widget && (
            <EVEntry {...moduleEntity(widget)} removeTopSpacing={true} removeBottomSpacing={true} />
          )}
        </div>
      </div>
    </form>
  )
}

export default Form
